import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, ChevronDown, ChevronLeft } from "lucide-react";
import Logo from "@/components/Logo";

const NavBar = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    setIsScrolled(false);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 px-3 py-0.5 md:py-0.5 md:px-6",
        location.pathname !== '/' 
          ? "bg-white shadow-md" 
          : isScrolled 
            ? "bg-white shadow-md" 
            : "bg-transparent"
      )}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="z-50 flex items-center">
          <a href={isHomePage ? "#" : "/"} className="flex items-center">
            {(location.pathname !== '/' || isScrolled) ? (
              <img 
                src="/lovable-uploads/5c26e582-ada1-4949-83d0-4080ede6952e.png" 
                alt="Gavinsurance Logo" 
                className="h-10 md:h-12"
              />
            ) : (
              <img 
                src="/lovable-uploads/7ff57c71-9f0c-4c38-9662-15f7f08f60cc.png" 
                alt="Gavinsurance Logo White" 
                className="h-10 md:h-12"
              />
            )}
          </a>
          
          {!isHomePage && (
            <Link 
              to="/" 
              className="ml-4 flex items-center text-sm font-medium text-[#0284c7] hover:text-[#0c4a6e] transition-colors"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Volver al inicio
            </Link>
          )}
        </div>

        <div className="hidden md:flex items-center space-x-6">
          <div className="relative group">
            <button className={cn(
              "flex items-center text-sm font-medium transition-colors",
              (location.pathname !== '/' || isScrolled) 
                ? "text-[#0284c7] hover:text-[#0c4a6e]" 
                : "text-white hover:text-white/80"
            )}>
              Seguros
              <ChevronDown className="ml-1 h-4 w-4" />
            </button>
            <div className="absolute left-0 top-full w-48 bg-white shadow-lg rounded-md overflow-hidden hidden group-hover:block border border-[#e0f2fe]">
              <a href="#" className="block px-4 py-2 text-sm text-[#0c4a6e] hover:bg-[#f0f9ff]">Seguro de Vida</a>
              <a href="#" className="block px-4 py-2 text-sm text-[#0c4a6e] hover:bg-[#f0f9ff]">Seguro de Salud</a>
              <a href="#" className="block px-4 py-2 text-sm text-[#0c4a6e] hover:bg-[#f0f9ff]">Seguro Dental</a>
              <a href="#" className="block px-4 py-2 text-sm text-[#0c4a6e] hover:bg-[#f0f9ff]">Seguro de Visión</a>
              <a href="#" className="block px-4 py-2 text-sm text-[#0c4a6e] hover:bg-[#f0f9ff]">Seguro Suplementario</a>
            </div>
          </div>
          
          {isHomePage && (
            <>
              <a 
                href="#features" 
                className={cn(
                  "text-sm font-medium transition-colors",
                  (location.pathname !== '/' || isScrolled) 
                    ? "text-[#0284c7] hover:text-[#0c4a6e]" 
                    : "text-white hover:text-white/80"
                )}
              >
                Servicios
              </a>
              <a 
                href="#about" 
                className={cn(
                  "text-sm font-medium transition-colors",
                  (location.pathname !== '/' || isScrolled) 
                    ? "text-[#0284c7] hover:text-[#0c4a6e]" 
                    : "text-white hover:text-white/80"
                )}
              >
                Nosotros
              </a>
              <a 
                href="#contact" 
                className={cn(
                  "text-sm font-medium transition-colors",
                  (location.pathname !== '/' || isScrolled) 
                    ? "text-[#0284c7] hover:text-[#0c4a6e]" 
                    : "text-white hover:text-white/80"
                )}
              >
                Contacto
              </a>
            </>
          )}
          
          <a 
            href="tel:+17865695142" 
            className={cn(
              "inline-flex items-center rounded-md px-4 h-8 shadow-md text-xs",
              (location.pathname !== '/' || isScrolled)
                ? "bg-gradient-to-r from-[#0284c7] to-[#0ea5e9] hover:from-[#0c4a6e] hover:to-[#0284c7] text-white"
                : "bg-white hover:bg-white/90 text-[#0284c7]"
            )}
          >
            <Phone className="mr-2 h-3 w-3" />
            (888) 370-8454
          </a>
        </div>

        <button 
          className="md:hidden z-50" 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? (
            <X className={cn("h-6 w-6", (location.pathname !== '/' || isScrolled) ? "text-[#0c4a6e]" : "text-white")} />
          ) : (
            <Menu className={cn("h-6 w-6", (location.pathname !== '/' || isScrolled) ? "text-[#0c4a6e]" : "text-white")} />
          )}
        </button>

        <div 
          className={cn(
            "fixed inset-0 bg-white/95 backdrop-blur-md flex flex-col items-center justify-center space-y-8 md:hidden transition-all duration-300 ease-in-out",
            isMobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          )}
        >
          {!isHomePage && (
            <Link
              to="/"
              className="flex items-center text-xl font-medium text-[#0284c7]"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <ChevronLeft className="h-5 w-5 mr-2" />
              Volver al inicio
            </Link>
          )}
          
          <a
            href="#"
            className="text-xl font-medium text-[#0c4a6e]"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Seguros
          </a>
          
          {isHomePage && (
            <>
              <a
                href="#features"
                className="text-xl font-medium text-[#0c4a6e]"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Servicios
              </a>
              <a
                href="#about"
                className="text-xl font-medium text-[#0c4a6e]"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Nosotros
              </a>
              <a
                href="#contact"
                className="text-xl font-medium text-[#0c4a6e]"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contacto
              </a>
            </>
          )}
          
          <a 
            href="tel:+17865695142" 
            className="inline-flex items-center rounded-md px-8 py-2 mt-4 bg-gradient-to-r from-[#0284c7] to-[#0ea5e9] hover:from-[#0c4a6e] hover:to-[#0284c7] text-white shadow-md"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <Phone className="mr-2 h-4 w-4" />
            (888) 370-8454
          </a>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
